import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import axiosInstance from '../../utils/axios';

const columnConfig = [
  { key: 1, value: 'laydown', hidden: false },
  { key: 2, value: 'delivered_qty', hidden: false },
  { key: 3, value: 'damaged_qty', hidden: false },
  { key: 4, value: 'remediated_qty', hidden: false },
  { key: 5, value: 'current_qty', hidden: false }
];

const getColumns = () => {
  return columnConfig
    .filter(col => !col.hidden)
    .map(col => {
      switch (col.value) {
        case 'laydown':
          return { 
            field: 'name', 
            headerName: 'Laydown Name', 
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: false
          };
        case 'delivered_qty':
          return { 
            field: 'total_received_quantity', 
            headerName: 'Delivered Qty', 
            flex: 1,
            type: 'number',
            headerAlign: 'center',
            align: 'center',
            sortable: false
          };
        case 'damaged_qty':
          return { 
            field: 'damaged_quantity', 
            headerName: 'Damaged Qty', 
            flex: 1,
            type: 'number',
            headerAlign: 'center',
            align: 'center',
            sortable: false
          };
        case 'remediated_qty':
          return { 
            field: 'remediated_quantity', 
            headerName: 'Remediated Qty', 
            flex: 1,
            type: 'number',
            headerAlign: 'center',
            align: 'center',
            sortable: false
          };
        case 'current_qty':
          return { 
            field: 'current_quantity', 
            headerName: 'Current Qty', 
            flex: 1,
            type: 'number',
            headerAlign: 'center',
            align: 'center',
            sortable: false
          };
        default:
          return null;
      }
    })
    .filter(Boolean);
};

const QuantityDialog = ({ open, onClose, rowData }) => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (open && rowData) {
      fetchTableData(rowData);
    }
  }, [open, rowData]);

  useEffect(() => {
    if (!open) {
      setList([]);
      setRows([]);
    }
  }, [open]);

  useEffect(() => {
    if (list && typeof list === 'object') {
      const transformedData = Object.keys(list).map(key => ({
        id: key, 
        laydown_id: list[key].laydown_id,
        name: list[key].name,
        total_received_quantity: list[key].total_received_quantity || 0,
        current_quantity: list[key].current_quantity || 0,
        remediated_quantity: list[key].remediated_quantity || 0,
        damaged_quantity: list[key].damaged_quantity || 0
      }));
      setRows(transformedData);
    }
  }, [list]);

  const fetchTableData = async (value) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/projects/inventory/laydown-count/agg?bom_id=${value?.bom_id}`
      );
      if (res.data) {
        setList(res.data);
      }
    } catch (err) {
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const columns = getColumns();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Quantity Overview
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Typography fontSize={18} gutterBottom>
              {rowData?.description || rowData?.item_code || 'Item Details'}
            </Typography>
            
            <Box sx={{ width: '100%', mt: 2 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                hideFooter={rows.length <= 5}
                pageSizeOptions={[5, 10, 25]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 5 },
                  },
                }}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: 'white',
                      fontWeight: 600,
                    },
                    '& .MuiDataGrid-iconSeparator': {
                      color: 'white',
                    },
                    '& .MuiDataGrid-sortIcon': {
                      color: 'white',
                    },
                    '& .MuiDataGrid-menuIcon': {
                      color: 'white',
                    }
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Overview of quantities across different laydowns for this item.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuantityDialog;